import { useMemo } from "react";
import classnames from "classnames";
import { useResponsive } from "@outplayed/responsive";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { getTierColor } from "@ugg/shared/utils/tier-helpers";
import { numberWithCommas, round } from "@ugg/shared/utils/math";
import { ChampionLink } from "@ugg/shared/components/TierList/common/ChampionLink";
import { RoleC, getRoleImg, normalizeRole } from "@ugg/shared/utils/role-helpers";
import { TooltipContainer } from "@outplayed/tooltips";
import { useTable, useSortBy, Column } from "react-table";
import { TierListTableError } from "@ugg/shared/components/TierList/common/TierListTableError";
import { DuosInfo } from "@ugg/shared/api/data-parser/champions/duos";

function getDuoHeader(dataFor: string) {
  return (
    <TooltipContainer tooltipInfo={`Duo's ${dataFor} Difference at 15 minutes`}>
      <div>{dataFor} @15</div>
    </TooltipContainer>
  );
}

interface ChampionDuosTableProps {
  data: DuosInfo["duos"];
}

export function ChampionDuosTable(props: ChampionDuosTableProps) {
  const { getChampionName } = getRiotAssetsContext();
  const { data } = props;
  const { currentBreakpoint } = useResponsive();

  const isMobileSmall = ["MOBILE_SMALL", "MOBILE_MEDIUM"].includes(currentBreakpoint);

  const tableData = useMemo(() => {
    return data || [];
  }, [data]);

  const columns = useMemo<Column<DuosInfo["duos"][number]>[]>(
    () => [
      {
        id: "rank",
        accessor: "champion_id",
        Header: <span>Rank</span>,
        responsiveClassName: "min-w-[50px] max-w-[50px]",
        headerClassName: "rank max-xs:invisible",
        className: "rank",
        Cell: ({ index }) => <span>{index + 1}</span>,
        sortable: false,
      },
      {
        Header: <span>Role</span>,
        accessor: "role",
        responsiveClassName: "min-w-[40px] max-w-[55px] max-xs:!hidden",
        headerClassName: "role",
        Cell: (props) => (
          <div>
            <img className="w-[20px] h-[20px]" src={getRoleImg(normalizeRole(props.value))} alt={normalizeRole(props.value)} />
          </div>
        ),
        sortable: false,
      },
      {
        Header: <span>Duo Champion</span>,
        headerClassName: "duo_champion max-xs:[&>*]:hidden",
        responsiveClassName: `
          xxxs:min-w-[70px] xs:min-w-[155px]
          max-xs:!min-w-[70px] 
          max-xs:!flex-[70] max-xs:!justify-center
          min-xs:!justify-start max-xs:!min-w-[70px] 
          max-xs:!flex-[70]
        `,
        accessor: "champion_link",
        className: "champion",
        sortType: (a, b) => {
          const champNameA = getChampionName(a.values.champion_link.champion_id);
          const champNameB = getChampionName(b.values.champion_link.champion_id);
          return champNameB.localeCompare(champNameA);
        },
        Cell: ({ cell: { value } }) => (
          <ChampionLink
            className="max-xs:[&_.champion-name]:hidden max-xs:!pl-0 xs:[&_.role]:hidden"
            championId={value.champion_id}
            role={RoleC.convertToString(value.role)}
            dim={isMobileSmall ? 24 : 30}
            showRoleIcon
          />
        ),
      },
      // {
      //   Header: "Tier",
      //   headerClassName: "tier",
      //   accessor: "tier",
      //   id: "tier",
      //   className: "tier",
      //   minWidth: defaultColWidth,
      //   sortMethod: (a, b) => {
      //     if(a.pick_rate >= .5 && b.pick_rate >= .5) {
      //       if(a.win_rate > b.win_rate) return 1;
      //       else if(a.win_rate < b.win_rate) return -1;
      //       return 0;
      //     }
      //     else if(a.pick_rate >= .5 && b.pick_rate < .5) {
      //       return 1;
      //     }
      //     else if(a.pick_rate < .5 && b.pick_rate >= .5) {
      //       return -1;
      //     }
      //     else if(a.pick_rate < .5 && b.pick_rate < .5) {
      //       if(a.win_rate > b.win_rate) return 1;
      //       else if(a.win_rate < b.win_rate) return -1;
      //       return 0;
      //     }
      //     return 0;
      //   },
      //   Cell: props => {
      //     if(props.value.pick_rate < 0.5) {
      //       return <span><b>?</b></span>
      //     }
      //     return (
      //       <span className={getTierColor(props.value.win_rate)}>
      //         <b>{getTier(props.value.win_rate)}</b>
      //       </span>
      //     )
      //   }
      // },
      {
        Header: "Duo Win Rate",
        headerClassName: "win_rate",
        accessor: "win_rate",
        responsiveClassName: "min-w-[100px] max-w-[100px]",
        className: "win_rate",
        id: "winrate",
        sortType: (a, b) => {
          return a.values.winrate - b.values.winrate;
        },
        Cell: ({ cell: { value } }) => (
          <span className={getTierColor(round(value, 2))}>
            <b>{round(value, 2) + "%"}</b>
          </span>
        ),
        sortDescFirst: true,
      },
      {
        Header: getDuoHeader("Gold"),
        accessor: "gold_adv_15",
        headerClassName: "gold",
        className: "gold",
        responsiveClassName: "min-w-[80px] max-w-[100px]",
        sortType: (a, b) => {
          return a.values.gold_adv_15 - b.values.gold_adv_15;
        },
      },
      {
        Header: getDuoHeader("XP"),
        accessor: "xp_adv_15",
        headerClassName: "xp",
        responsiveClassName: "min-w-[80px] max-w-[100px]",
        className: "xp",
        sortType: (a, b) => {
          return a.values.xp_adv_15 - b.values.xp_adv_15;
        },
      },
      {
        Header: getDuoHeader("Kills"),
        accessor: "kill_adv_15",
        headerClassName: "kill",
        responsiveClassName: "min-w-[80px] max-w-[100px]",
        className: "kill",
        sortType: (a, b) => {
          return a.values.kill_adv_15 - b.values.kill_adv_15;
        },
      },
      {
        Header: getDuoHeader("CS"),
        accessor: "cs_adv_15",
        headerClassName: "cs",
        responsiveClassName: "min-w-[80px] max-w-[100px]",
        className: "cs",
        sortType: (a, b) => {
          return a.values.cs_adv_15 - b.values.cs_adv_15;
        },
      },
      {
        Header: getDuoHeader("Team Gold"),
        accessor: "team_gold_difference_15",
        headerClassName: "team_gold",
        className: "team_gold",
        responsiveClassName: "min-w-[100px] max-w-[110px]",
        sortType: (a, b) => {
          return a.values.team_gold_difference_15 - b.values.team_gold_difference_15;
        },
        Cell: (props) => <span>{numberWithCommas(round(props.value))}</span>,
      },
      {
        Header: "Matches",
        accessor: "matches",
        headerClassName: "matches",
        className: "matches",
        responsiveClassName: "min-w-[80px] max-w-[100px]",
        sortDescFirst: true,
        Cell: ({ cell: { value } }) => <span>{numberWithCommas(round(value))}</span>,
      },
    ],
    [currentBreakpoint],
  );

  const columnStyle = () => ({
    flex: `1 1 100%`,
  });

  const { state, getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: tableData,
      initialState: {
        sortBy: [{ id: "winrate", desc: true }],
      },
      disableSortRemove: true,
    },
    useSortBy,
  );

  if (tableData.length === 0) {
    return <TierListTableError />;
  }

  return (
    <div>
      <div className="w-full overflow-x-auto rounded-sm bg-purple-400">
        <div
          {...getTableProps({
            className: "content-section ReactTable ugg-table-2 duos-list-table xs:min-w-[950px] max-sm:min-w-[750px]",
          })}
        >
          <div className={"rt-thead -header"}>
            {headerGroups.map((headerGroup) => (
              <div {...headerGroup.getHeaderGroupProps({ className: "rt-tr" })}>
                {headerGroup.headers.map((column) => {
                  const thClassNames = classnames([
                    "rt-th",
                    column.headerClassName,
                    column.responsiveClassName,
                    column.isSorted && !column.isSortedDesc ? "-sort-asc" : "",
                    column.isSorted && column.isSortedDesc ? "-sort-desc" : "",
                  ]);

                  return (
                    <div
                      className={thClassNames}
                      {...column.getHeaderProps(
                        column.getSortByToggleProps({
                          style: columnStyle(),
                        }),
                      )}
                    >
                      {column.render("Header")}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          <div {...getTableBodyProps({ className: "rt-tbody" })}>
            {rows.map((row, i) => {
              prepareRow(row);

              return (
                <div key={i} className="rt-tr-group">
                  <div {...row.getRowProps({ className: "rt-tr" })}>
                    {row.cells.map((cell, index) => {
                      const tdClassNames = classnames("rt-td", cell.column.responsiveClassName, cell.column.className, {
                        sorted: cell.column.isSorted,
                        "is-in-odd-row": i % 2 === 0,
                      });

                      return (
                        <div
                          {...cell.getCellProps({
                            className: tdClassNames,
                            style: columnStyle(),
                          })}
                        >
                          {cell.render("Cell", { index: i })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
